import { sanitizeUrl } from "@/lib/helpers";
import type { SearchKind } from "./kinds";
import type { SearchResults } from "./searchKinds";

/** One result as a compact row: the navbar panel's shape. */
export type SearchHit = { key: string; href: string; title: string; sub?: string; image?: string; round?: boolean; ltrSub?: boolean };

const str = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const img = (v: unknown) => sanitizeUrl(str(v)) || undefined;
/** The chain by name, as the tiles print it — the API says "matic" on one kind and "polygon" on another. */
const chain = (name: unknown, isFa: boolean) => (/matic|polygon/i.test(String(name ?? "")) ? (isFa ? "پالیگان" : "Polygon") : name ? (isFa ? "اتریوم" : "Ethereum") : undefined);

/**
 * Every kind's row — each link built the way that kind's own tile builds it,
 * so a result opens the same page from the panel as from anywhere else.
 */
export function toHits(kind: SearchKind, r: SearchResults, lang: "fa" | "en"): SearchHit[] {
  const p = (path: string) => `/${lang}${path}`;
  const isFa = lang === "fa";
  switch (kind) {
    case "user":
      return r.user.map((a) => ({ key: `u${a.id}`, href: p(`/artists/${encodeURIComponent(a.username || String(a.id))}`), title: a.name || a.username, sub: a.username ? `@${a.username}` : undefined, ltrSub: true, image: img(a.profile?.cover), round: true }));
    case "collection":
      // network_id rides along, as on the collection tiles — the page goes straight to the right chain.
      return r.collection.map((c) => ({ key: `c${c.id}`, href: p(`/collection/${c.contract_address}${c.network != null ? `?network_id=${c.network}` : ""}`), title: c.name, sub: chain(c.network_name, isFa), image: img(c.logo) }));
    case "nft":
      return r.nft.map((n) => ({ key: `n${n.id}`, href: p(`/assets/${n.network_name || "ethereum"}/${n.contract_address}/${n.token_id}`), title: n.name || `#${n.token_id}`, sub: chain(n.network_name, isFa), image: img(n.image) }));
    case "drops":
      return r.drops.map((d) => ({ key: `d${d.id}`, href: p(`/drops/${d.slug || d.id}`), title: d.title, sub: d.creator?.name, image: img(d.coverImage) }));
    case "auctions":
      return r.auctions.map((a) => ({ key: `a${a.id}`, href: p(`/auctions/${a.slug}`), title: a.title, sub: a.creator?.name || a.user?.name || undefined, image: img(a.image) }));
    case "galleries":
      return r.galleries.map((g) => ({ key: `g${g.id}`, href: p(`/galleries/${g.slug}`), title: g.title, sub: g.owner?.name || g.owner?.username, image: img(g.image) }));
    case "blogs":
      return r.blogs.map((b) => ({ key: `b${String(b.id)}`, href: p(str(b.slug) ? `/blogs/${str(b.slug)}` : "/blogs"), title: str(b.title) ?? "", image: img(b.mini_cover) ?? img(b.cover) }));
  }
}
